import { routePaths } from 'components/Routes/data';
import { BulkUploadTypes, UploadTypeConfigCallback } from 'constants/bulkUpload';
import permissions from 'constants/permissions';
import { getCurrentPayrollMonth } from 'components/RunPayroll/utils/utils';
import { WorkflowTypesMap } from 'components/ApprovalWorkflows/constants';
import { format, subMonths, addMonths } from 'date-fns';

export const bulkOvertimeAdditionConfig: UploadTypeConfigCallback = (data) => {
  // Bulk overtime rows route through the TYPE_RUNPAYROLL_EDIT approval workflow.
  const needsApproval = (data.actionsWithActiveWorkflows ?? []).includes(
    WorkflowTypesMap.EDIT_PAYROLL,
  );

  const getPayrollMonthOptions = () => {
    const currentMonth = getCurrentPayrollMonth();
    const months: Date[] = [];

    for (let i = -3; i <= 3; i++) {
      const month = i < 0 ? subMonths(currentMonth, Math.abs(i)) : addMonths(currentMonth, i);
      months.push(month);
    }

    return months.map((month) => ({
      value: format(month, 'yyyy-MM-dd'),
      label: format(month, 'MMMM yyyy'),
    }));
  };

  const urlParams = new URLSearchParams(data.history.location.search);
  const payrollMonth = urlParams.get('payrollMonth') || '';

  return {
    permissions: [permissions.BULK_ADDITION_DEDUCTION],
    initiation: {
      skipMonthChangeConfirmation: true,
      feature: 'overtime',
      permissions: [],
      pageTitle: 'Bulk Overtime',
      step1Title: '1. Download the template',
      step1Subtitle:
        'Download the template and open it in Excel. Fill in overtime details for employees, save the file, and upload it here.',
      instructions: [
        'Do not add or remove any columns.',
        'There is a limit of 500 employees per upload.',
        "If a field is empty, leave it blank (don't enter '-', 'NA,' etc.).",
        'Mandatory fields are mentioned with * sign.',
        'Date should be mentioned in DD/MM/YYYY format.',
      ],
      hasMonthSelector: true,
      dropdownSelectorLabel: 'Select payroll month',
      dropdownOptions: getPayrollMonthOptions(),
      onDropdownOptionChange(selectedMonth) {
        if (selectedMonth) {
          data.history.push({
            pathname: routePaths.bulkUploads.root,
            search: `?type=${BulkUploadTypes.TYPE_BULK_OVERTIME_ADDITION}&payrollMonth=${selectedMonth}`,
          });
        }
      },
      onSampleTemplateDownload() {
        const monthParam = payrollMonth
          ? payrollMonth
          : format(getCurrentPayrollMonth(), 'yyyy-MM-dd');
        window.open(
          `/v2/api/bulk-overtime/download-template?payroll_month=${monthParam}`,
          '_blank',
        );
      },
    },
    preview: {
      feature: 'overtime',
      permissions: [],
      columns: [
        {
          title: 'ID',
          render: (item) => item?.file_row?.commonProperties?.employeeId,
        },
        {
          title: 'Name',
          render: (item) => item?.file_row?.commonProperties?.employeeName,
        },
        {
          title: 'Email ID',
          render: (item) => item?.file_row?.commonProperties?.email,
        },
        {
          title: 'OT Type',
          render: (item) => item?.file_row?.formulaCardName ?? '-',
        },
        {
          title: 'Total OT',
          render: (item) => {
            const dailyEntries = item?.file_row?.dailyEntries;
            if (!dailyEntries?.length) return '-';
            const total = dailyEntries.reduce(
              (sum: number, entry) => sum + Number(entry.value || 0),
              0,
            );
            const unit = item?.file_row?.unit === 'days' ? 'days' : 'hrs';
            return `${total} ${unit}`;
          },
        },
      ],
      renderConfirmationText: (details) =>
        `Almost done! Please confirm to proceed with the bulk overtime upload for ${details.total_number_of_rows} employees.`,
      successModalCtaText: needsApproval ? 'Go to Approvals' : 'Go to Run Payroll',
      successModalMainText: needsApproval
        ? 'Request to update employee overtime has been sent successfully 🎉'
        : 'Overtime data uploaded successfully',
      successModalSubText: () =>
        needsApproval
          ? 'Track the request(s) from the Approvals page.'
          : 'You can view the updated overtime details in the Run Payroll section.',
      onSuccessModalCtaClick() {
        if (needsApproval) {
          data.history.push(
            routePaths.approvalWorkflows.myRequests.editPayroll.initiatedByMe,
          );
        } else {
          data.history.push(routePaths.runpayroll);
        }
      },
      onDownloadErrorReport(history) {
        history?.push({
          pathname: routePaths.bulkUploads.initiate,
          search: `type=${BulkUploadTypes.TYPE_BULK_OVERTIME_ADDITION}`,
        });
      },
    },
  };
};
